import { useState } from 'react';
import { Input } from '@/components/ui/input.jsx';
import { toast } from '@/components/ui/toaster.jsx';
import { usePeekStore } from '@/stores/peek-store.js';
import { openContactPeek } from '@/lib/links.js';
import { personIdentitySender } from '@/components/account/record/factWriters.js';
import { writerFor } from './doors.js';
import { PERSON_EDIT_WORDS } from './words.js';

// DAVID'S EDIT OF A PERSON'S NAME, EMAIL OR PHONE, ALWAYS OPEN (people-create-door
// C5, ruling 1: identity is edited in TRACON, never in CRM). The PHF answer edit (shared/AnswerEdit)
// pattern without a reason: the person edit door asks none. It posts through
// doors.js writerFor (the person's Contact) and the factWriters sender, the one
// client path to PATCH /api/tracon/person/:contactId/identity, with only what
// changed; a name posts as its parts. Each warning is one toast.info, and the
// page re-reads (onSaved) so the row shows what the store holds. A name the FAA
// registry confirmed needs a reason (AR13): the person sheet holds that, so the
// control opens it. A person with no Contact has no edit here, and says so.
// Guard: tests/person-edit-surfaces.test.js.
const NAME_PARTS = [['first_name', 'first', 'First name'], ['middle_name', 'middle', 'Middle name'], ['last_name', 'last', 'Last name']];

export default function PersonIdentityEdit({ fact_ref, field, ctx, label, value, parts, onSaved }) {
  const send = personIdentitySender(writerFor(fact_ref, ctx));
  const openPeek = usePeekStore((s) => s.openPeek);
  const isName = field === 'name';
  const seed = () => (isName ? Object.fromEntries(NAME_PARTS.map(([k, from]) => [k, parts?.[from] || ''])) : { [field]: value || '' });
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(seed);
  const [saving, setSaving] = useState(false);
  if (!send) return <span className="ml-3 text-xs text-text-dim" data-person-edit="no-contact">{PERSON_EDIT_WORDS.no_contact}</span>;
  if (!open) {
    return (
      <button type="button" onClick={() => { setDraft(seed()); setOpen(true); }} className="ml-3 inline-flex min-h-[36px] items-center text-xs text-text-dim underline-offset-2 hover:text-text hover:underline" data-person-edit="closed">
        {PERSON_EDIT_WORDS.open}
      </button>
    );
  }
  const before = seed();
  const changed = Object.fromEntries(Object.entries(draft).map(([k, v]) => [k, v.trim()]).filter(([k, v]) => v !== before[k]));
  const save = async (e) => {
    e.preventDefault();
    if (saving) return;
    if (!Object.keys(changed).length) { setOpen(false); return; }
    setSaving(true);
    try {
      const r = await send(changed);
      for (const w of (r?.warnings || [])) toast.info(typeof w === 'string' ? w : (w.message || String(w)));
      toast.success(`${label}: ${PERSON_EDIT_WORDS.saved}`);
      setOpen(false);
      onSaved?.();
    } catch (err) {
      if (err?.body?.code === 'registry_confirmed') { toast.info(PERSON_EDIT_WORDS.registry); setOpen(false); openPeek(openContactPeek(writerFor(fact_ref, ctx).target.contact_id)); }
      else toast.error(err?.body?.error || err?.message || PERSON_EDIT_WORDS.failed);
    } finally { setSaving(false); }
  };
  const set = (k) => (e) => setDraft((d) => ({ ...d, [k]: e.target.value }));
  return (
    <form onSubmit={save} className="mt-1 flex flex-wrap items-center gap-2" data-person-edit="open">
      {isName
        ? NAME_PARTS.map(([k, , l], i) => <Input key={k} value={draft[k]} onChange={set(k)} placeholder={l} aria-label={l} className="min-h-[36px] w-36 text-sm" disabled={saving} autoFocus={i === 0} />)
        : <Input value={draft[field]} onChange={set(field)} placeholder={label} aria-label={`${label}, new value`} className={`min-h-[36px] w-56 text-sm ${field === 'phone' ? 'font-mono' : ''}`} disabled={saving} autoFocus />}
      <button type="submit" disabled={saving} className="min-h-[36px] rounded-card border border-accent bg-accent px-3 text-sm font-medium text-white disabled:opacity-60">{saving ? 'Saving' : 'Save edit'}</button>
      <button type="button" disabled={saving} onClick={() => setOpen(false)} className="min-h-[36px] px-2 text-sm text-text-dim hover:text-text">Cancel</button>
    </form>
  );
}
